import { TooltipContent } from "@/components/ui/tooltip";
import { ClickToCopy } from "@/components/ClickToCopy";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Case,
  OutputDocument,
  OutputDocumentType,
} from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import { loadAndFormatDate } from "@/utils/dateFormat";
import { StatusBadge } from "@/components/Status";
import { DataTable } from "@/components/table/Table";
import { useEffect } from "react";
import { useAuthInfo } from "@propelauth/react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, PlusIcon } from "lucide-react";
import { toast } from "sonner";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  createOutputDocument,
  getDocumentTypes,
} from "@/utils/apiCalls";
import { Dialog } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const CreateOutputDocument = (props: {
  setCase: React.Dispatch<React.SetStateAction<Case | null>>;
  existingDocuments: OutputDocument[];
  case: Case;
}) => {
  const authInfo = useAuthInfo();
  const [open, setOpen] = useState(false);
  const [docTypes, setDocTypes] = useState<OutputDocumentType[]>([]);
  const [selectedDocType, setSelectedDocType] =
    useState<OutputDocumentType | null>(null);

  // Create a set of existing document type names for easy lookup
  const existingDocTypeNames = new Set(
    props.existingDocuments.map((doc) => doc.document_type_name)
  );

  useEffect(() => {
    getDocumentTypes(
      props.case.clinician.specialty,
      authInfo.accessToken ?? null
    ).then((response) => {
      if (response !== null) {
        setDocTypes(response);
      } else {
        toast.error("Failed to fetch document types, please try again");
      }
    });
  }, [props.case.clinician.specialty]);

  const handleCreateNewOutputDocument = async (docTypeId: string) => {
    const selectedDocType = docTypes.find(
      (docType) => docType.id === docTypeId
    );

    // Check if it's a QME document and if any documents are selected for QME sections
    if (selectedDocType?.name === "QME Report") {
      const hasSelectedQMEDocuments = props.case.input_documents.some(
        (doc) => doc.qme_sections === true
      );
      if (!hasSelectedQMEDocuments) {
        toast.error(
          "Please select at least one document for QME sections before creating a QME Report"
        );
        return;
      }
    }

    const response = await createOutputDocument(
      props.case.id,
      docTypeId,
      authInfo.accessToken ?? null
    );
    if (response !== null) {
      props.setCase((prev) => {
        if (prev === null) {
          return null;
        }
        // Create a new case object with a new output_documents array
        return {
          ...prev,
          output_documents: [...prev.output_documents, response],
        };
      });
      toast.success("Output document created");
      setOpen(false);
    } else {
      toast.error("Failed to create output document, please try again");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="w-4 h-4" />
          Create Output Document
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Output Document</DialogTitle>
        </DialogHeader>
        <Select
          value={selectedDocType?.id}
          onValueChange={(value) =>
            setSelectedDocType(
              docTypes.find((docType) => docType.id === value) ?? null
            )
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select Document Type" />
          </SelectTrigger>
          <SelectContent>
            {docTypes.map((docType) => {
              const isExisting = existingDocTypeNames.has(docType.name);
              return (
                <Tooltip key={docType.id}>
                  <TooltipTrigger asChild>
                    <div>
                      <SelectItem
                        value={docType.id}
                        disabled={isExisting}
                        className={
                          isExisting ? "text-muted-foreground line-through" : ""
                        }
                      >
                        {docType.name} {isExisting && "(already exists)"}
                      </SelectItem>
                    </div>
                  </TooltipTrigger>
                  {isExisting && (
                    <TooltipContent>
                      <p>This document type already exists for this case</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </SelectContent>
        </Select>
        <Button
          disabled={selectedDocType === null}
          onClick={() =>
            handleCreateNewOutputDocument(selectedDocType?.id ?? "")
          }
        >
          Create
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const OutputDocumentTable = (props: {
  case: Case;
  setCase: React.Dispatch<React.SetStateAction<Case | null>>;
}) => {
  const navigate = useNavigate();

  const columns: ColumnDef<OutputDocument>[] = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ row }: any) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <ClickToCopy
                text={row.original.id}
                className="max-w-[50px] text-ellipsis overflow-hidden whitespace-nowrap"
              />
            </TooltipTrigger>
            <TooltipContent>{row.original.id}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "date",
      header: "Date",
      cell: ({ row }: any) => {
        return <div>{loadAndFormatDate(row.original.created_at)}</div>;
      },
    },
    {
      accessorKey: "document_type_name",
      header: "Document Type",
      cell: ({ row }: any) => {
        return <div>{row.original.document_type_name}</div>;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => {
        return <StatusBadge status={row.original.status} />;
      },
    },
    {
      id: "controls",
      cell: ({ row }) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(
                    `/case/${props.case.id}/output-document/${row.original.id}`
                  );
                }}
              >
                Open Document
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-md text-muted-foreground">
          {props.case.output_documents.length} documents
        </div>
        <div className="flex items-center space-x-2">
          <CreateOutputDocument
            setCase={props.setCase}
            existingDocuments={props.case.output_documents}
            case={props.case}
          />
        </div>
      </div>
      <DataTable
        data={props.case.output_documents}
        columns={columns}
        onRowClick={(row) => {
          navigate(`/case/${props.case.id}/output-document/${row.original.id}`);
        }}
      />
    </div>
  );
};
